import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { 
  Card, 
  Typography, 
  Button, 
  Space, 
  Form,
  Row,
  Col,
  Input,
  Select,
  message
} from 'antd';
import { 
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'; // useSearchParams 추가
import { academyTeacherApi } from '@/services/api';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';

const { Title, Text } = Typography;

const formatDateTime = (value?: string) => {
  if (!value) return '';
  const parsed = dayjs(value);
  if (parsed.isValid()) {
    return parsed.format('YYYY-MM-DD HH:mm');
  }
  const normalized = value.replace('T', ' ');
  return normalized.length > 16 ? normalized.slice(0, 16) : normalized;
};

interface TeacherDetailData {
  id: string;
  loginId: string;
  academyCode: string;
  name: string;
  birthDate: string;
  phone: string;
  email: string;
  address: string;
  status: string;
  registrationStatus: string;
  applicationDate: string;
  registrationDate: string;
  withdrawnAt?: string;
  subject: string;
  experience: number;
  education: string;
  introduction: string;
}

const TeacherDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { teacherId } = useParams<{ teacherId: string }>();
  const [searchParams] = useSearchParams(); // URL 파라미터 읽기
  const [loading, setLoading] = useState(true);
  const [teacherData, setTeacherData] = useState<TeacherDetailData | null>(null);
  const [form] = Form.useForm();
  const fromPath = (location.state as { from?: string } | null)?.from;

  // 진입 경로에 따른 목록으로 이동 함수
  const handleBackToList = () => {
    const from = searchParams.get('from');
    const academyCode = searchParams.get('academyCode');
    if (fromPath) {
      navigate(fromPath);
      return;
    }
    if (from === 'teacher-list') {
      navigate('/admin/teacher');
      return;
    }
    if (from === 'academy-detail' && academyCode) {
      navigate(`/admin/academy/detail/${academyCode}?tab=teachers`);
      return;
    }
    navigate(-1);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      await academyTeacherApi.update(Number(teacherId), {
        name: values.name,
        phone: values.phone,
        birthDate: values.birthDate || null,
      });
      message.success('저장되었습니다.');
      // 최신 데이터 재조회
      const res = await academyTeacherApi.detail(String(teacherId));
      const d = (res && (res as any).data) ? (res as any).data : res;
      const mapped: TeacherDetailData = {
        id: String(d.id ?? ''),
        loginId: String(d.loginId ?? ''),
        academyCode: d.academyCode ?? '',
        name: d.name ?? '',
        birthDate: d.birthDate ?? '',
        phone: d.phone ?? '',
        email: d.email ?? '',
        address: '',
        status: d.status ?? '',
        registrationStatus: '승인',
        applicationDate: d.registrationDate ?? '',
        registrationDate: d.registrationDate ?? '',
        withdrawnAt: formatDateTime(d.withdrawnAt),
        subject: '',
        experience: 0,
        education: '',
        introduction: '',
      };
      setTeacherData(mapped);
      form.setFieldsValue(mapped as any);
    } catch (e: any) {
      message.error(e?.response?.data?.message || '저장 실패');
    }
  };

  const handleWithdraw = async () => {
    if (!teacherId) return;
    const ok = window.confirm('해당 강사를 퇴원 처리하시겠습니까?');
    if (!ok) return;
    try {
      await academyTeacherApi.withdraw(teacherId);
      message.success('퇴원 처리되었습니다.');
      handleBackToList();
    } catch (e: any) {
      message.error(e?.response?.data?.message || '퇴원 처리 실패');
    }
  };

  useEffect(() => {
    const fetchDetail = async () => {
      if (!teacherId) return;
      setLoading(true);
      try {
        const res = await academyTeacherApi.detail(teacherId);
        const d = (res && (res as any).data) ? (res as any).data : res;
        const mapped: TeacherDetailData = {
          id: String(d.id ?? ''),
          loginId: String(d.loginId ?? ''),
          academyCode: d.academyCode ?? '',
          name: d.name ?? '',
          birthDate: d.birthDate ?? '',
          phone: d.phone ?? '',
          email: d.email ?? '',
          address: '',
          status: d.status ?? '',
          registrationStatus: '승인',
        applicationDate: d.registrationDate ?? '',
        registrationDate: d.registrationDate ?? '',
        withdrawnAt: formatDateTime(d.withdrawnAt),
          subject: '',
          experience: 0,
          education: '',
          introduction: '',
        };
        setTeacherData(mapped);
        // 폼 리셋
        form.setFieldsValue(mapped as any);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [teacherId, form]);

  if (loading) {
    return (
      <AdminLayout>
        <div>로딩 중...</div>
      </AdminLayout>
    );
  }

  if (!teacherData) {
    return (
      <AdminLayout>
        <div>강사 정보를 찾을 수 없습니다.</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Space style={{ marginBottom: 16 }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBackToList} // 수정된 함수 사용
            >
              목록으로
            </Button>
          </Space>
          
          <Title level={2}>강사 상세</Title>
          <Text type="secondary">강사ID: {teacherData.loginId}</Text>
        </div>

        {/* 상세 정보 */}
        <Card>
          <Form
            form={form}
            layout="vertical"
            initialValues={teacherData}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="강사ID" name="loginId">
                  <Input disabled size="large" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="학원코드" name="academyCode">
                  <Input disabled size="large" />
                </Form.Item>
              </Col>
            </Row>
 

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  label="이름" 
                  name="name"
                  rules={[
                    { required: true, message: '이름을 입력해주세요' },
                    { min: 2, max: 50, message: '이름은 2~50자로 입력해주세요' },
                    { pattern: /^[가-힣a-zA-Z\s]+$/, message: '한글, 영문, 공백만 입력 가능합니다' }
                  ]}
                >
                  <Input placeholder="김교사" size="large" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="생년월일"
                  name="birthDate"
                  rules={[
                    { required: true, message: '생년월일을 입력해주세요' },
                    { pattern: /^\d{4}-\d{2}-\d{2}$/, message: 'YYYY-MM-DD 형식으로 입력해주세요' },
                  ]}
                >
                  <Input placeholder="1990-01-01" size="large" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  label="연락처" 
                  name="phone"
                  rules={[
                    { required: true, message: '연락처를 입력해주세요' },
                    { pattern: /^010-\d{4}-\d{4}$/, message: '010-1234-5678 형식으로 입력해주세요' }
                  ]}
                >
                  <Input placeholder="010-1234-5678" size="large" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="가입상태" name="registrationStatus">
                  <Select disabled size="large">
                    <Select.Option value="승인">승인</Select.Option>
                    <Select.Option value="대기">대기</Select.Option>
                    <Select.Option value="반려">반려</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

            </Row>



            <Row gutter={16}>

              <Col span={12}>
                <Form.Item label="강사상태" name="status">
                  <Input disabled size="large" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="가입신청일" name="applicationDate">
                  <Input disabled size="large" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="퇴원 처리일" name="withdrawnAt">
                  <Input disabled size="large" placeholder="-" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="가입승인일" name="registrationDate">
                  <Input disabled size="large" />
                </Form.Item>
              </Col>
            </Row>
                {/* 버튼 */}
                <div style={{ textAlign: 'right', marginTop: 32 }}>
                  <Space size="middle">
                    <Button 
                      danger 
                      size="large"
                      style={{ height: 48, fontSize: 16, padding: '0 32px' }}
                      onClick={handleWithdraw}
                    >
                      퇴원
                    </Button>
                    <Button 
                      type="primary" 
                      size="large"
                      style={{ height: 48, fontSize: 16, padding: '0 32px' }}
                      onClick={handleSave}
                    >
                      저장
                    </Button>
                  </Space>
                </div>
              </Form>
            </Card>
          </div>
        </AdminLayout>
      );
    };

    export default TeacherDetail; 